import { DepartmentResource } from '@/entities/Department/Department'
import { transformToDepartment } from '@/entities/Department/DepartmentTransformers'
import { FetchResult, FetchResultType } from '@/entities/FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '@/entities/NotificationCenter/NotificationCenter'
import { PersonResource } from '@/entities/Person/Person'
import { transformToPerson } from '@/entities/Person/PersonTransformers'
import { RoleResource } from '@/entities/Role/Role'
import { transformToRole } from '@/entities/Role/RoleTransformers'
import { authorizedFetch } from '@/helpers/authorizedFetch'
import { trans } from '@/helpers/trans'
import { generateApiUrl, generateApiV3Url } from '@/helpers/url'
import { getIncluded } from '@/japi/getIncluded'
import { getMultipleIncluded } from '@/japi/getMultipleIncluded'
import { isResourceDocument } from '@/japi/guards/isResourceDocument'
import { isResourceOfType } from '@/japi/guards/isResourceOfType'
import { JapiDocument } from '@/japi/types/Document'

import { User, UserFormData, UserResource } from './User'
import { transformToUser, transformUserFormDataToEditUserProfileRequest } from './UserTransformers'

export const patchUserProfileApiCall = async (
    formData: UserFormData,
    userId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<User, string>> => {
    const apiData = transformUserFormDataToEditUserProfileRequest(formData, userId)
    const includes = ['person', 'roles', 'departments']

    try {
        const url = generateApiUrl({
            endpoint: `/users/${userId}`,
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify({ data: apiData }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<UserResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const departmentsResource = getMultipleIncluded(doc, doc.data, 'departments', isResourceOfType<DepartmentResource>('departments'))
        const rolesResource = getMultipleIncluded(doc, doc.data, 'roles', isResourceOfType<RoleResource>('roles'))
        const personResource = getIncluded(doc, doc.data, 'person', isResourceOfType<PersonResource>('people'))

        if (! personResource) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const departments = (departmentsResource || []).map(transformToDepartment)
        const roles = (rolesResource || []).map(transformToRole)
        const person = transformToPerson(personResource)
        const user = transformToUser(
            doc.data,
            person,
            departments,
            roles,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: user,
        }
    } catch (error) {
        console.error('[patchUserProfileApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

interface UploadUserAvatarResponse {
    image?: unknown
}

/**
 * Stores the avatar (a JPEG of at most 5 MB) as the Stream user's image and resolves to its signed CDN url.
 */
export const uploadUserAvatarApiCall = async (avatar: Blob): Promise<FetchResult<string, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: '/users/profile/me/avatar' })
        const body = new FormData()

        body.append('avatar', avatar, 'avatar.jpg')

        const response = await authorizedFetch(url, {
            method: 'POST',
            body,
            disableContentType: true,
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const { image }: UploadUserAvatarResponse = await response.json()

        if (typeof image !== 'string') {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: image,
        }
    } catch (error) {
        console.error('[uploadUserAvatarApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
